import React from "react";
import { motion } from "framer-motion";
import { User, Phone, Mail, Tag, FileText, Lock } from "lucide-react";

export const CRM = () => {
  return (
    <section className="py-20 sm:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">CRM that talks back</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Every chat, contact and deal in <span className="bg-gradient-to-r from-blue-600 to-emerald-500 bg-clip-text text-transparent">one place</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-xl">
            Wappit&apos;s built-in CRM auto-captures every conversation, enriches contacts with custom fields, and syncs two-way with your existing tools.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { Icon: User, t: "360° contact timeline", d: "See every message, order and note next to the chat." },
              { Icon: Tag, t: "Tags, lists & lifecycle", d: "Slice your audience however you want." },
              { Icon: FileText, t: "Notes & assignments", d: "Collaborate with your team in private threads." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="flex gap-4">
                <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center ring-4 ring-blue-100/60 flex-shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{t}</div>
                  <div className="text-slate-600 text-[15px]">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
          data-testid="crm-product-mockup"
        >
          {/* Browser-style frame around the real Wappit Shared Team Inbox screenshot */}
          <div className="relative rounded-3xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)] overflow-hidden">
            {/* Window chrome */}
            <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <div className="ml-3 hidden sm:flex items-center gap-2 rounded-md bg-white border border-slate-200 px-2.5 py-1 text-[11px] text-slate-500 font-medium">
                <Lock className="h-3 w-3 text-emerald-600" />
                app.wappit.com / inbox
              </div>
              <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
                Live product
              </span>
            </div>
            {/* Screenshot */}
            <img
              src="/brand/inbox.png?v=2"
              alt="Wappit Shared Team Inbox — chat list, conversation panel, agent assignment and labels"
              className="block w-full h-auto select-none"
              draggable={false}
              loading="lazy"
            />
            {/* Subtle inner glow */}
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-slate-900/5 rounded-3xl" />
          </div>

          {/* Floating chips */}
          <motion.div
            initial={{ opacity: 0, x: -16, y: 8 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, type: "spring", stiffness: 220, damping: 22 }}
            className="hidden sm:flex absolute -left-3 top-12 items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg px-3 py-1.5"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[11px] font-semibold text-slate-700">36 unread</span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 16, y: 8 }}
            whileInView={{ opacity: 1, x: 0, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, type: "spring", stiffness: 220, damping: 22 }}
            className="hidden sm:flex absolute -right-3 bottom-14 items-center gap-2 rounded-full bg-white border border-slate-200 shadow-lg px-3 py-1.5"
          >
            <Tag className="h-3 w-3 text-blue-600" />
            <span className="text-[11px] font-semibold text-slate-700">Auto-assigned</span>
          </motion.div>

          {/* Floating WhatsApp-style notification popup */}
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, duration: 0.5 }}
            data-testid="crm-floating-notification"
            className="hidden md:flex absolute -bottom-4 -left-4 max-w-[260px] items-start gap-3 rounded-2xl bg-white border border-slate-200 shadow-xl px-3.5 py-3"
          >
            <div className="h-9 w-9 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M12 2C6.48 2 2 6.16 2 11.27c0 1.84.58 3.55 1.6 5L2 22l5.94-1.55A10.4 10.4 0 0 0 12 21.5c5.52 0 10-4.16 10-9.27S17.52 2 12 2z"/></svg>
            </div>
            <div className="leading-tight">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">New WhatsApp lead</div>
              <div className="text-sm font-semibold text-slate-900">Ananya from Mumbai</div>
              <div className="text-[11px] text-slate-500">Hi, interested in your Diwali offer!</div>
            </div>
            <span className="ml-auto text-[10px] text-slate-400 flex-shrink-0">now</span>
          </motion.div>

          <p className="mt-3 text-center text-[11px] text-slate-500">
            Real Wappit Shared Team Inbox · names and numbers anonymised
          </p>
        </motion.div>
      </div>
    </section>
  );
};
